import { useCallback } from "react";

/**
 * Does this browser have the popover API?
 *
 * Checked at call time rather than captured once at module load so a test can
 * stub the capability in either direction, and so the answer is never baked in
 * before the DOM exists.
 */
export function isTopLayerSupported(): boolean {
  return (
    typeof HTMLElement !== "undefined" &&
    typeof HTMLElement.prototype.showPopover === "function"
  );
}

/**
 * GH-1600 (D1) — promote an element into the browser's **top layer** using the
 * popover API, instead of competing for an ever-larger `z-index`.
 *
 * The incoming-call banner kept losing that competition: `PresentationPlayer`
 * paints an opaque `z-index: 999999` surface across the viewport and portalled
 * dialogs sit at 10000, so a call could ring while its banner was buried —
 * audible, but unanswerable. The top layer sits outside the page's z-index
 * space altogether: it also paints above a fullscreen element, and it cannot be
 * out-bid later by a component picking a bigger number.
 *
 * `manual`, never `auto`: an `auto` popover light-dismisses on Escape and on an
 * outside click, and auto popovers close one another. A ringing call must not
 * disappear because the user pressed Escape or clicked the page behind it.
 *
 * The returned `popover` value is `undefined` where the API is missing. That is
 * deliberate and load-bearing: the UA stylesheet hides `[popover]` elements
 * that are not open, so emitting the attribute on a browser that cannot open it
 * would make the element *invisible* — strictly worse than the plain `z-index`
 * behaviour it replaces. Without the attribute the element simply renders as it
 * always did.
 */
export function useTopLayer<T extends HTMLElement = HTMLElement>(): {
  ref: (node: T | null) => void;
  popover: "manual" | undefined;
} {
  const ref = useCallback((node: T | null) => {
    // No attribute means the capability check above declined; showPopover()
    // would throw NotSupportedError.
    if (!node || !node.hasAttribute("popover")) return;
    if (typeof node.showPopover !== "function") return;
    try {
      node.showPopover();
    } catch {
      // Already in the top layer — React StrictMode detaches and re-attaches
      // the ref against the same live node — or the node left the document
      // before the ref ran. Neither is recoverable and neither needs to be:
      // the element still renders, it just isn't (re-)promoted.
    }
  }, []);

  return { ref, popover: isTopLayerSupported() ? "manual" : undefined };
}
